// src/lib/fishiq/fishIQConfidenceModel.ts
// D.7 confidence model.
// Ranking decides ordering/presence. Confidence explains trust in the match.
// Keep those systems separate so ranking boosts cannot masquerade as evidence.

export type FishIQSpeciesConfidenceLabel =
  | 'Strong fit'
  | 'Good fit'
  | 'Possible fit'
  | 'Limited evidence';

export type FishIQSpeciesConfidenceComponent = {
  key: 'regional' | 'habitat' | 'behavior' | 'environment' | 'local' | 'source';
  label: string;
  score: number;
  available: boolean;
  details: string[];
};

export type FishIQSpeciesConfidenceBreakdown = {
  score: number;
  label: FishIQSpeciesConfidenceLabel;
  components: FishIQSpeciesConfidenceComponent[];
  reasons: string[];
  cappedBy?: string;
};

type BuildSpeciesConfidenceInput = {
  species: any;
  areaEvidence?: any | null;
  environment?: any | null;
};

const clean = (value: any) => String(value ?? '').trim();
const lower = (value: any) => clean(value).toLowerCase();
const clamp100 = (value: number) => Math.max(0, Math.min(100, Math.round(value)));

const unique = (items: string[], limit = 6) => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    const value = clean(raw);
    const key = value.toLowerCase();
    if (!value || seen.has(key)) continue;
    seen.add(key);
    out.push(value);
    if (out.length >= limit) break;
  }
  return out;
};

const hasReason = (reasons: string[], pattern: RegExp) => reasons.some((reason) => pattern.test(reason));

const regionalScoreFor = (species: any, reasons: string[]) => {
  const signal = species?.__fishiq?.regionalSignal;
  if (!signal) return 34;
  const label = lower(signal?.label);
  const boost = Number(signal?.scoreBoost || 0);
  if (/strong regional|known regional/.test(label)) return 92;
  if (/common regional|regional habitat|documented habitat/.test(label)) return 82;
  if (/stocked|seasonal/.test(label)) return 72;
  if (boost >= 30) return 84;
  if (boost >= 18) return 76;
  if (hasReason(reasons, /regional/i)) return 68;
  return 60;
};

const habitatScoreFor = (species: any, reasons: string[]) => {
  const environmental = species?.__fishiq?.environmentalSignal;
  let score = 42;
  if (hasReason(reasons, /habitat fit|water fit|water type|realm fit|stillwater|flowing-water|bay\/harbor|reef\/structure|backwater\/estuary/i)) score += 26;
  if (hasReason(reasons, /structure fit|structure-oriented profile/i)) score += 14;
  if (environmental?.bathymetryCompatible === true) score += 10;
  if (environmental?.bathymetryCompatible === false) score -= 30;
  return clamp100(score);
};

const behaviorScoreFor = (species: any, reasons: string[]) => {
  const environmental = species?.__fishiq?.environmentalSignal;
  let score = 44;
  if (hasReason(reasons, /season|dawn|dusk|night|day activity|active window/i)) score += 18;
  if (environmental?.currentCompatible === true) score += 12;
  if (environmental?.currentCompatible === false) score -= 24;
  if (Array.isArray(environmental?.reasons) && environmental.reasons.length) score += 8;
  return clamp100(score);
};

const sourceScoreFor = (species: any) => {
  const sourceLabel = lower(species?.__fishiq?.sourceLabel);
  const quality = species?.__fishiq?.canonicalDataQuality;
  const completeness = Number(quality?.completeness || 0);
  let score = 38;
  if (/local signal/.test(sourceLabel)) score = 92;
  else if (/regional profile/.test(sourceLabel)) score = 82;
  else if (/public source backed/.test(sourceLabel)) score = 78;
  else if (/curated/.test(sourceLabel)) score = 64;
  if (Number.isFinite(completeness) && completeness > 0) {
    score = Math.round(score * 0.72 + completeness * 0.28);
  }
  return clamp100(score);
};

const localScoreFor = (species: any, reasons: string[]) => {
  // Keep shared/local evidence distinct from personal catch history. Personal
  // history can personalize ranking and contributes through the Area Read's
  // personal bucket, but a catch from another water must not masquerade as a
  // privacy-safe local observation signal.
  if (hasReason(reasons, /Roddo local signal/i)) return 94;
  return 28;
};

const environmentScoreFor = (species: any, environment: any) => {
  const speciesEnv = species?.__fishiq?.environmentalSignal;
  if (!environment && !speciesEnv) return 30;

  const raw = Number(
    speciesEnv?.confidence ??
    environment?.environmentConfidence ??
    0,
  );
  let score = clamp100(raw * 100);
  if (!score) score = 35;

  const strength = lower(environment?.evidenceStrength);
  if (strength === 'strong') score = Math.max(score, 82);
  else if (strength === 'good') score = Math.max(score, 68);
  else if (strength === 'limited') score = Math.min(score, 58);

  if (speciesEnv?.compatible === false) score = Math.min(score, 38);
  return clamp100(score);
};

const labelFor = (score: number): FishIQSpeciesConfidenceLabel => {
  if (score >= 82) return 'Strong fit';
  if (score >= 66) return 'Good fit';
  if (score >= 48) return 'Possible fit';
  return 'Limited evidence';
};

export function buildFishIQSpeciesConfidence(
  input: BuildSpeciesConfidenceInput,
): FishIQSpeciesConfidenceBreakdown {
  const species = input.species || {};
  const rank = species?.__fishiq?.locationRank || {};
  const reasons = Array.isArray(rank?.reasons)
    ? rank.reasons.map(clean).filter(Boolean)
    : [];
  const areaEvidence = input.areaEvidence || null;

  const regional = regionalScoreFor(species, reasons);
  const habitat = habitatScoreFor(species, reasons);
  const behavior = behaviorScoreFor(species, reasons);
  const environment = environmentScoreFor(species, input.environment);
  const local = localScoreFor(species, reasons);
  const source = sourceScoreFor(species);

  const components: FishIQSpeciesConfidenceComponent[] = [
    { key: 'regional', label: 'Regional fit', score: regional, available: Boolean(species?.__fishiq?.regionalSignal), details: unique(species?.__fishiq?.regionalSignal?.reasons || []) },
    { key: 'habitat', label: 'Habitat fit', score: habitat, available: true, details: unique(reasons.filter((r) => /habitat|water|structure|reef|stillwater|flowing-water/i.test(r))) },
    { key: 'behavior', label: 'Behavior fit', score: behavior, available: true, details: unique(reasons.filter((r) => /season|dawn|dusk|night|day|current/i.test(r))) },
    { key: 'environment', label: 'Water Intel quality', score: environment, available: Boolean(input.environment || species?.__fishiq?.environmentalSignal), details: unique(species?.__fishiq?.environmentalSignal?.supportingReads || []) },
    { key: 'local', label: 'Local evidence', score: local, available: local > 28, details: unique(reasons.filter((r) => /local signal|catch history|delayed local reads|contributing anglers/i.test(r))) },
    { key: 'source', label: 'Source quality', score: source, available: true, details: unique([species?.__fishiq?.sourceLabel || '']) },
  ];

  // Regional/habitat are the foundation. Environment and behavior refine the
  // tactical confidence. Local observations can strengthen trust, but never
  // create geographic eligibility because the ranker already owns that gate.
  let score =
    regional * 0.28 +
    habitat * 0.24 +
    behavior * 0.16 +
    environment * 0.14 +
    source * 0.10 +
    local * 0.08;

  const areaScore = Number(areaEvidence?.score);
  if (Number.isFinite(areaScore)) {
    score = score * 0.78 + areaScore * 0.22;
  }

  let cappedBy = '';
  if (species?.__fishiq?.environmentalSignal?.compatible === false) {
    score = Math.min(score, 54);
    cappedBy = 'Live water does not fully match this species profile';
  } else if (areaEvidence?.hasConfirmedWater === false) {
    score = Math.min(score, 54);
    cappedBy = 'Water context is still being resolved';
  } else if (areaEvidence?.fallbackOnly && !species?.__fishiq?.regionalSignal) {
    score = Math.min(score, 58);
    cappedBy = 'Guide knowledge only';
  } else if (areaEvidence?.fallbackOnly) {
    score = Math.min(score, 69);
    cappedBy = 'No qualifying local observation sample yet';
  }

  score = clamp100(score);
  const topComponents = [...components]
    .filter((component) => component.available)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  const reasonsOut = unique([
    ...topComponents.map((component) => `${component.label}: ${component.score}%`),
    cappedBy,
  ], 4);

  return {
    score,
    label: labelFor(score),
    components,
    reasons: reasonsOut,
    ...(cappedBy ? { cappedBy } : {}),
  };
}
